import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Select,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useBreakpointValue,
  useColorModeValue,
  VStack,
} from "@chakra-ui/react";
import axios from "../../axiosConfig";
import { useParams, Link } from "react-router-dom";
import { TriangleDownIcon, TriangleUpIcon } from "@chakra-ui/icons";
import ListPageLayout from "../common/ListPageLayout";
import TruncatedText from "../common/TruncatedText";
import {
  formatOrganizationSurveyTitle,
  getSurveyPeriodLabel,
} from "../../utils/organizationSurveyHelper";

const SURVEY_MAX_CHARS = 100;
const PERIOD_MAX_CHARS = 80;

const OrganizationSurveys = () => {
  const { organizationId } = useParams();
  const [organization, setOrganization] = useState(null);
  const [surveyRecords, setSurveyRecords] = useState([]);
  const [loadingOrg, setLoadingOrg] = useState(true);
  const [loadingSurveys, setLoadingSurveys] = useState(true);

  const isMobile = useBreakpointValue({ base: true, md: false });

  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const subTextColor = useColorModeValue("gray.600", "gray.400");

  // ✅ Sorting (same standard as other lists)
  const [sortKey, setSortKey] = useState("survey"); // survey | period | language | status
  const [sortDir, setSortDir] = useState("asc"); // asc | desc

  useEffect(() => {
    const fetchOrganization = async () => {
      try {
        const response = await axios.get(`/organizations/${organizationId}`);
        setOrganization(response.data);
      } catch (error) {
        console.error("Error fetching organization:", error);
      } finally {
        setLoadingOrg(false);
      }
    };

    const fetchSurveys = async () => {
      try {
        const response = await axios.get(`/organizations/${organizationId}/organization_surveys`);
        const formatted = (response.data ?? []).map((survey) => ({
          organizationSurveyId: survey.organization_survey_id ?? survey.id,
          name: survey.survey?.name,
          survey: survey.survey,
          survey_period: survey.survey_period,
          language: survey.survey?.language,
          periodKey: survey.survey_period?.key,
          periodName: survey.survey_period?.name,
          locked: survey.locked,
        }));

        setSurveyRecords(formatted);
      } catch (error) {
        console.error("Error fetching surveys:", error);
      } finally {
        setLoadingSurveys(false);
      }
    };

    fetchOrganization();
    fetchSurveys();
  }, [organizationId]);

  /* ---------------- sorting helpers ---------------- */

  const normalize = (v) => (v ?? "").toString().trim().toLowerCase();

  const periodLabel = (s) => getSurveyPeriodLabel(s);
  const surveyTitle = (s) => formatOrganizationSurveyTitle(s);
  const statusLabel = (s) => (s.locked ? "locked" : "active");

  const sortedSurveyRecords = useMemo(() => {
    const dir = sortDir === "asc" ? 1 : -1;

    const valueFor = (s) => {
      switch (sortKey) {
        case "period":
          return normalize(periodLabel(s));
        case "language":
          return normalize(s.language);
        case "status":
          return statusLabel(s);
        case "survey":
        default:
          return normalize(s.name);
      }
    };

    return [...surveyRecords].sort((a, b) => {
      const av = valueFor(a);
      const bv = valueFor(b);

      const cmp = av.localeCompare(bv);
      if (cmp !== 0) return cmp * dir;

      // stable fallback
      return normalize(a.name).localeCompare(normalize(b.name));
    });
  }, [surveyRecords, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const SortIndicator = ({ active }) => {
    if (!active) return null;
    return sortDir === "asc" ? (
      <TriangleUpIcon ml={2} boxSize="10px" />
    ) : (
      <TriangleDownIcon ml={2} boxSize="10px" />
    );
  };

  /* ---------------- UI renderers ---------------- */

  const StatusBadge = ({ locked }) => (
    <Badge
      colorScheme={locked ? "red" : "green"}
      variant="subtle"
      borderRadius="md"
    >
      {locked ? "Locked" : "Active"}
    </Badge>
  );

  const renderCard = (s) => (
    <Box
      key={s.organizationSurveyId}
      p={4}
      border="1px solid"
      borderColor={borderColor}
      borderRadius="xl"
      shadow="sm"
      bg={cardBg}
      _hover={{ shadow: "md" }}
    >
      <VStack align="start" spacing={3} w="100%">
        <HStack justify="space-between" w="100%" align="start">
          <Box minW={0} w="100%">
            <TruncatedText
              value={surveyTitle(s) || "—"}
              maxChars={SURVEY_MAX_CHARS}
              fontWeight="bold"
              fontSize="md"
            />
            <Text fontSize="sm" color={subTextColor}>
              Period: {periodLabel(s) || "—"}
            </Text>
          </Box>

          <StatusBadge locked={s.locked} />
        </HStack>

        <HStack spacing={2} flexWrap="wrap">
          <Badge variant="subtle" colorScheme="blue" borderRadius="md">
            Lang: {s.language || "—"}
          </Badge>
        </HStack>

        <HStack w="100%" justify="flex-end">
          <Tooltip label="Configure survey" hasArrow>
            <Button
              as={Link}
              to={`/app/organizations/${organizationId}/surveys/${s.organizationSurveyId}/configure`}
              colorScheme="cyan"
              size="sm"
              variant="outline"
            >
              Edit
            </Button>
          </Tooltip>
        </HStack>
      </VStack>
    </Box>
  );

  const renderTable = () => (
    <Box
      w="100%"
      maxW="100%"
      minW={0}
      border="1px solid"
      borderColor={borderColor}
      borderRadius="xl"
      bg={cardBg}
      shadow="sm"
      overflow="hidden"
    >
      <TableContainer w="100%" maxW="100%" minW={0} overflowX="auto">
        <Table
          variant="unstyled"
          size="md"
          tableLayout="fixed"
          w="100%"
          maxW="100%"
          sx={{
            "th, td": { borderBottom: "1px solid", borderColor: "gray.100" },
            "tbody tr:hover": { background: "gray.50" },
          }}
        >
          <colgroup>
            <col style={{ width: "40%" }} />
            <col style={{ width: "26%" }} />
            <col style={{ width: "14%" }} />
            <col style={{ width: "10%" }} />
            <col style={{ width: "10%" }} />
          </colgroup>

          <Thead>
            <Tr
              sx={{
                position: "sticky",
                top: 0,
                zIndex: 1,
                background: cardBg,
              }}
            >
              <Th
                cursor="pointer"
                userSelect="none"
                onClick={() => toggleSort("survey")}
                py={4}
                fontSize="xs"
                letterSpacing="wider"
                textTransform="uppercase"
              >
                Survey <SortIndicator active={sortKey === "survey"} />
              </Th>

              <Th
                cursor="pointer"
                userSelect="none"
                onClick={() => toggleSort("period")}
                py={4}
                fontSize="xs"
                letterSpacing="wider"
                textTransform="uppercase"
              >
                Period <SortIndicator active={sortKey === "period"} />
              </Th>

              <Th
                cursor="pointer"
                userSelect="none"
                onClick={() => toggleSort("language")}
                py={4}
                fontSize="xs"
                letterSpacing="wider"
                textTransform="uppercase"
              >
                Language <SortIndicator active={sortKey === "language"} />
              </Th>

              <Th
                cursor="pointer"
                userSelect="none"
                onClick={() => toggleSort("status")}
                py={4}
                fontSize="xs"
                letterSpacing="wider"
                textTransform="uppercase"
              >
                Status <SortIndicator active={sortKey === "status"} />
              </Th>

              <Th py={4} fontSize="xs" letterSpacing="wider" textTransform="uppercase">
                Actions
              </Th>
            </Tr>
          </Thead>

          <Tbody>
            {sortedSurveyRecords.map((s) => (
              <Tr key={s.organizationSurveyId}>
                <Td py={3}>
                  <Box minW={0} w="100%">
                    <TruncatedText
                      value={surveyTitle(s) || "—"}
                      maxChars={SURVEY_MAX_CHARS}
                      fontWeight="semibold"
                    />
                  </Box>
                </Td>

                <Td py={3}>
                  <Box minW={0} w="100%">
                    <TruncatedText
                      value={periodLabel(s) || "—"}
                      maxChars={PERIOD_MAX_CHARS}
                    />
                  </Box>
                </Td>

                <Td py={3}>
                  <Box minW={0} w="100%">
                    <TruncatedText value={s.language || "—"} tooltip={false} />
                  </Box>
                </Td>

                <Td py={3}>
                  <StatusBadge locked={s.locked} />
                </Td>

                <Td py={3}>
                  <Button
                    as={Link}
                    to={`/app/organizations/${organizationId}/surveys/${s.organizationSurveyId}/configure`}
                    colorScheme="cyan"
                    size="sm"
                    variant="outline"
                  >
                    Edit
                  </Button>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  );

  const actions = (
    <HStack spacing={3} flexWrap="wrap">
      {isMobile && (
        <Select
          size="sm"
          w="240px"
          value={`${sortKey}:${sortDir}`}
          onChange={(e) => {
            const [k, d] = e.target.value.split(":");
            setSortKey(k);
            setSortDir(d);
          }}
        >
          <option value="survey:asc">Survey (A–Z)</option>
          <option value="survey:desc">Survey (Z–A)</option>
          <option value="period:asc">Period (A–Z)</option>
          <option value="period:desc">Period (Z–A)</option>
          <option value="language:asc">Language (A–Z)</option>
          <option value="language:desc">Language (Z–A)</option>
          <option value="status:asc">Status (Active→Locked)</option>
          <option value="status:desc">Status (Locked→Active)</option>
        </Select>
      )}

      <Button
        as={Link}
        to={`/app/organizations/${organizationId}/surveys/assign`}
        colorScheme="green"
        variant="outline"
        size="sm"
      >
        Assign Survey
      </Button>
    </HStack>
  );

  const title = loadingOrg ? "Loading school..." : organization?.name || "School";
  const subtitle = "Manage Surveys";

  const isLoading = loadingOrg || loadingSurveys;

  return (
    <ListPageLayout title={title} actions={actions} contentProps={{ maxW: "100%", minW: 0 }}>
      <Text fontSize="md" color={subTextColor} mb={4}>
        {subtitle}
      </Text>

      {isLoading ? (
        <Flex justify="center" mt={10}>
          <Spinner size="xl" color="orange.400" />
        </Flex>
      ) : sortedSurveyRecords.length > 0 ? (
        isMobile ? (
          <VStack spacing={4} align="stretch">
            {sortedSurveyRecords.map(renderCard)}
          </VStack>
        ) : (
          renderTable()
        )
      ) : (
        <Text mt={10} textAlign="center">
          No surveys associated.
        </Text>
      )}
    </ListPageLayout>
  );
};

export default OrganizationSurveys;
